//================================================================
//  Component: New Initiative Form
//================================================================

//  Purpose: This contains the form for a new initiative or technology request

//  Properties:
//    - collectionid = {string, Firestore collection id}
//    - documentid = {string, Firestore document id}
//    - task = {object, Firestore document}

//  Example:
//    <NewInitiativeForm
//      collectionid={collectionid}
//      documentid={documentid}
//      task={task}
//    ></NewInitiativeForm>    

// ==============================================================

// Libraries
import React from 'react';

// Contexts

// Components
import LiveField from '../../../../../Components/LiveField/LiveField';
import DynamicAddLinkLive from '../../../../../Components/DynamicAddLinkLive/DynamicAddLinkLive';
import FileUploadLiveField from '../../../../../Components/FileUploadLiveField/FileUploadLiveField';
import DigitalSMEInitiativeLive from '../../../../../Components/DigitalSMEInitiativeLive/DigitalSMEInitiativeLive';

// Images
import InfoIcon from '../../../../../Components/Images/Icon_Info_Blue.svg';

// CSS

export default function NewInitiativeForm({
    collectionid,
    documentid,
    task
}) {

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    return (
        <div>

            {/* ------------- New Initiative or Technology ------------- */}

            <h5 className='text-[18px] text-teal-600 mb-2'> New Initiative or Technology </h5>

            {/*  1. Vendor Risk Rating (UpGuard)*/}
            <div className='Form-Row w-full lg:w-[700px]'>
                <label className='font-medium'> 1. Vendor Risk Rating (UpGuard) </label>
                <div className='flex gap-2 rounded-lg border-1 border-[#0972D3] bg-[#E9F7FF] px-3 py-2 items-center'>
                    <img src={InfoIcon} alt='Info icon' width={25} height={25}></img>
                    <p className='mb-0 text-[#424242]'>
                        To generate a Vendor Risk Rating, go to “Diligence & Governance” and create a task.
                    </p>
                </div>
            </div>

            {/* 2.  Does this initiative relate to any of the following? Select all that apply. */}
            <DigitalSMEInitiativeLive
                initiativeCategories={task?.demandassessment?.initiativecategories || []}
                collectionid={collectionid}
                documentid={documentid}
                fieldpath={'demandassessment.initiativecategories'}
                disabled={(task?.status === 'Completed' || task?.status === 'Rejected' || task?.status === 'Cancelled' ) ? true : false}
            ></DigitalSMEInitiativeLive>

            {/* 3. Does this request align to the Business / Functions / Digital technology strategy? */}
            <LiveField
                type={'choice'}
                collectionid={collectionid}
                documentid={documentid}
                fieldpath={'demandassessment.businessfunctiontechalignment'}
                defaultValue={task?.demandassessment?.businessfunctiontechalignment}
                styleInput={{width: '355px'}}
                label={<label className='font-medium'> 3. Does this request align to the Business / Functions / Digital technology strategy? </label>}
                options={[
                    'Yes',
                    'No',
                    'Unknown',
                    'N/A',
                ]}
                disabled={(task?.status === 'Completed' || task?.status === 'Rejected' || task?.status === 'Cancelled' ) ? true : false}
            ></LiveField>

            {/* 4. Please provide any relevant commentary regarding strategy alignment */}
            <LiveField
                type={'multiline'}
                collectionid={collectionid}
                documentid={documentid}
                fieldpath={'demandassessment.strategyalignmentcommentary'}
                label={<label className='font-medium'> 4. Please provide any relevant commentary regarding strategy alignment</label>}
                defaultValue={task?.demandassessment?.strategyalignmentcommentary}
                placeholder={'Summarise strategy alignment'}
                styleInput={{maxWidth: '625px'}}
                disabled={(task?.status === 'Completed' || task?.status === 'Rejected' || task?.status === 'Cancelled' ) ? true : false}
            ></LiveField>

            {/*  5. Has an options analysis been undertaken? What were the outcomes? */}
            <LiveField
                type={'multiline'}
                collectionid={collectionid}
                documentid={documentid}
                fieldpath={'demandassessment.optionsanalysis'}
                label={<label className='font-medium'> 5. Has an options analysis been undertaken? What were the outcomes?</label>}
                defaultValue={task?.demandassessment?.optionsanalysis}
                placeholder={'Leave blank if unknown or not applicable'}
                styleInput={{maxWidth: '625px'}}
                disabled={(task?.status === 'Completed' || task?.status === 'Rejected' || task?.status === 'Cancelled' ) ? true : false}
            ></LiveField>

            {/* 6. What is the Digital recommendation for this request? */}
            <LiveField
                type={'multiline'}
                collectionid={collectionid}
                documentid={documentid}
                fieldpath={'demandassessment.digitalrecommendation'}
                label={<label className='font-medium'>6. What is the Digital recommendation for this request?</label>}
                defaultValue={task?.demandassessment?.digitalrecommendation}
                placeholder={'Based on engagement to date, does Digital endorse the scope of this request?'}
                styleInput={{maxWidth: '625px'}}
                disabled={(task?.status === 'Completed' || task?.status === 'Rejected' || task?.status === 'Cancelled' ) ? true : false}
            ></LiveField>

            {/*  7. Please upload or link any relevant documents in support of this request such as Business case, requirements documentation etc.*/}
            <div className='mt-3'>
                <label className='font-medium'> 7. Please upload or link any relevant documents in support of this request such as Business case, requirements documentation etc.</label>

                {/* Attach Files */}
                <FileUploadLiveField
                    label={<label className='font-medium'> Attach documents </label>}
                    defaultValue={task?.demandassessment?.attacheddocuments}
                    collectionid={collectionid}
                    documentid={documentid}
                    fieldpath={'demandassessment.attacheddocuments'}
                    disabled={(task?.status === 'Completed' || task?.status === 'Rejected' || task?.status === 'Cancelled' ) ? true : false}
                ></FileUploadLiveField>

                {/* Add links */}
                <div>
                    <label className='font-medium mb-2'> Add links </label>
                    <DynamicAddLinkLive
                        links={task?.demandassessment?.links}
                        collectionid={collectionid}
                        documentid={documentid}
                        fieldpath={'demandassessment.links'}
                        disabled={(task?.status === 'Completed' || task?.status === 'Rejected' || task?.status === 'Cancelled' ) ? true : false}
                    ></DynamicAddLinkLive>
                </div>
            </div>

        </div>
    );

}